import { useId, useState } from "react";

import { errorMessage, useConsentLedger, useRecordConsent } from "../../api/queries";
import type {
  ConsoleConfig,
  Consent,
  Dataset,
  EvidenceKind,
  Purpose,
} from "../../api/types";
import { PURPOSES } from "../../api/types";
import { useStepUp } from "../../auth/useStepUp";
import { kindInASentence, trainingKindsRequired } from "../../lib/evidence";
import { formatDateTime, formatSeconds, joinWords } from "../../lib/format";
import { PURPOSE_LABELS, PURPOSE_SENTENCES } from "../../lib/metrics";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { ServerLines } from "../ui/ServerLines";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../ui/StateBlock";
import { EvidenceFields } from "./EvidenceFields";

/**
 * The one thing a consent change cannot do, in the console's own words.
 *
 * It is printed next to the button **before** the change as well as after it:
 * a consent change reaches every future request instantly, and reaches a URL
 * somebody already holds not at all. Both numbers come from `GET /config` —
 * a deployment that shortens its links changes this sentence without a
 * release.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *
 * Returns:
 *     The sentence, or the shorter honest form while the numbers are unknown.
 */
function honestWindow(config: ConsoleConfig | undefined): string {
  if (!config) {
    return (
      "Links already issued keep working until they expire. Nothing revokes a " +
      "URL that is already in someone's terminal."
    );
  }
  return (
    `Links already issued keep working until they expire — ` +
    `${formatSeconds(config.access_ttl.default)} for a link that took the default, ` +
    `${formatSeconds(config.access_ttl.max)} at the very most. Nothing revokes a URL ` +
    `that is already in someone's terminal.`
  );
}

interface ChangeConsentDialogProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
  onClose: () => void;
}

/**
 * Record the whole new list of what a client allows.
 *
 * Not a diff and not a toggle: the request carries the list in full, because a
 * request that says "remove training" leaves the reader of the ledger guessing
 * what the state was. Narrowing goes through immediately; widening is refused
 * until a code is confirmed, and the dialog then repeats the request.
 *
 * Args:
 *     dataset: The dataset whose consent is changing.
 *     config: The published configuration, for the honest-window sentence.
 *     onClose: Close the dialog.
 *
 * Returns:
 *     The dialog, or the code prompt while one is being asked for.
 */
function ChangeConsentDialog({
  dataset,
  config,
  onClose,
}: ChangeConsentDialogProps): JSX.Element {
  const evidenceId = useId();
  const [permissions, setPermissions] = useState<Purpose[]>(
    dataset.consent.permissions,
  );
  const [evidence, setEvidence] = useState("");
  // The kind belongs to the source being recorded now, not to the row in
  // force, so it starts empty like the evidence beside it: carrying the old
  // row's label onto a new source is how a note becomes a DPA by inertia.
  const [evidenceKind, setEvidenceKind] = useState<EvidenceKind | "">("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [lines, setLines] = useState<string[] | null>(null);
  const record = useRecordConsent(dataset.id);
  const stepUp = useStepUp();

  const widening = permissions.some(
    (purpose) => !dataset.consent.permissions.includes(purpose),
  );
  const trainingNeeds = trainingKindsRequired(
    config,
    permissions,
    evidenceKind,
  );

  const submit = (): void => {
    if (evidenceKind === "") {
      return;
    }
    const payload = {
      permissions,
      evidence,
      evidence_kind: evidenceKind,
      evidence_ref: evidenceRef.trim() ? evidenceRef.trim() : null,
    };
    record.mutate(payload, {
      onSuccess: (consent: Consent) => setLines(consent.lines ?? []),
      onError: (error) => {
        stepUp.capture(error, () => submit());
      },
    });
  };

  // Only ever one dialog on screen: the code prompt replaces this one and
  // gives it back afterwards, with everything typed still in it.
  if (stepUp.dialog) {
    return stepUp.dialog;
  }

  if (lines) {
    return (
      <Dialog
        title="Recorded"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <ServerLines lines={lines} />
      </Dialog>
    );
  }

  return (
    <Dialog
      title="What may we do with this client's data?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={
              record.isPending ||
              permissions.length === 0 ||
              evidence.trim().length === 0 ||
              evidenceKind === ""
            }
            onClick={submit}
          >
            {record.isPending ? "Recording…" : "Record this"}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        Tick what {dataset.account.name} agreed to. This replaces the list — what
        is not ticked is not allowed from the moment you record it.
      </p>

      <fieldset className="form__block">
        <legend className="sr-only">What the client allows</legend>
        {PURPOSES.map((purpose) => (
          <label className="check" key={purpose}>
            <input
              type="checkbox"
              checked={permissions.includes(purpose)}
              onChange={(event) =>
                setPermissions((current) =>
                  event.target.checked
                    ? [...current, purpose].sort(
                        (left, right) =>
                          PURPOSES.indexOf(left) - PURPOSES.indexOf(right),
                      )
                    : current.filter((item) => item !== purpose),
                )
              }
            />
            <span>{PURPOSE_SENTENCES[purpose]}</span>
          </label>
        ))}
      </fieldset>

      <Field
        label="Where did the client say so?"
        htmlFor={evidenceId}
        hint="A contract clause, an e-mail and its date, or a DPA or NDA reference. A consent with no source is somebody's memory."
        error={
          record.isError && !stepUp.dialog ? errorMessage(record.error) : undefined
        }
      >
        <input
          id={evidenceId}
          className="input"
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
          placeholder="Call with C. Meyer, 11 August 2026"
        />
      </Field>

      <EvidenceFields
        config={config}
        kind={evidenceKind}
        reference={evidenceRef}
        onKind={setEvidenceKind}
        onReference={setEvidenceRef}
      />

      {widening ? (
        <p className="dialog__warning">
          This adds something they did not allow before, so the console will ask
          for your code.
        </p>
      ) : null}

      {/*
        Said before the button rather than after the refusal, and the server
        stays the enforcer: the rule is published with its own reason, and this
        prints that reason rather than a second wording of it.
      */}
      {trainingNeeds && evidenceKind !== "" && config ? (
        <p className="dialog__warning">
          Training is ticked with {kindInASentence(config, evidenceKind)} as the
          source, and this console will refuse that.{" "}
          {config.training_requires_evidence_kind.reason} Record it with{" "}
          {joinWords(trainingNeeds)}, or record this without training.
        </p>
      ) : null}

      <p className="dialog__text">{honestWindow(config)}</p>
    </Dialog>
  );
}

interface ConsentSectionProps {
  dataset: Dataset;
  config: ConsoleConfig | undefined;
}

/**
 * The consent ledger: what a client allows, and where each answer came from.
 *
 * Append-only, superseded rows included, because "what were we allowed to do
 * in March" is the question this record exists to answer.
 *
 * Args:
 *     dataset: The dataset whose ledger this is.
 *     config: The published configuration, for the honest-window sentence.
 *
 * Returns:
 *     The consent section.
 */
export function ConsentSection({
  dataset,
  config,
}: ConsentSectionProps): JSX.Element {
  const ledger = useConsentLedger(dataset.id);
  const [changing, setChanging] = useState(false);
  const readOnly = dataset.deleted_at !== null;

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">What we may do with it</h2>
        {readOnly ? null : (
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setChanging(true)}
          >
            Change this
          </button>
        )}
      </div>

      <p className="section__lead">{honestWindow(config)}</p>

      {ledger.isPending ? (
        <LoadingBlock label="Reading the ledger…" />
      ) : ledger.isError ? (
        <ErrorBlock error={ledger.error} onRetry={() => void ledger.refetch()} />
      ) : ledger.data.items.length === 0 ? (
        <EmptyBlock label="Nothing has been recorded yet." />
      ) : (
        <ol className="ledger">
          {ledger.data.items.map((consent) => (
            <li
              className="ledger__row"
              data-current={consent.superseded_at === null}
              key={consent.id}
            >
              <span className="ledger__permissions">
                {consent.permissions.length > 0
                  ? joinWords(
                      consent.permissions.map(
                        (purpose) => PURPOSE_LABELS[purpose],
                      ),
                    )
                  : "nothing"}
                {consent.superseded_at === null ? (
                  <span className="ledger__badge">in force</span>
                ) : null}
              </span>
              {/*
                The console's own sentence fragment, printed word for word.
                Composing it here from the kind and the reference would be a
                second wording of the same fact, and the two would drift. Every
                row carries it, superseded ones included — a ledger that
                labelled only the current row could not answer what the
                permission we relied on in March rested on.
              */}
              <span className="ledger__source">{consent.source}</span>
              {/*
                The reference as an address, when it is one — the server
                derived it (ADR-0017). Fixed words, never the address itself:
                the console has not checked that the link resolves or that
                what is behind it is the signed paper, and the anchor asserts
                nothing. The click is the reader's own act.
              */}
              {consent.evidence_url ? (
                <a
                  className="ledger__open"
                  href={consent.evidence_url}
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  open the document
                </a>
              ) : null}
              <span className="ledger__evidence">{consent.evidence}</span>
              <span className="ledger__meta">
                {consent.recorded_by} · {formatDateTime(consent.at)}
                {consent.superseded_at
                  ? ` · replaced ${formatDateTime(consent.superseded_at)}`
                  : ""}
              </span>
            </li>
          ))}
        </ol>
      )}

      {changing ? (
        <ChangeConsentDialog
          dataset={dataset}
          config={config}
          onClose={() => setChanging(false)}
        />
      ) : null}
    </section>
  );
}
